import { z } from "zod";
import { ROLE_LABELS } from "../constants/roles.js";
import { nameSchema } from "./common.js";
import { levelsOf, reportsTo, resolveChain, roleKey, } from "./organization.js";
/**
 * Role-based access control (D-16).
 *
 * An org admin decides, per chain role and level, which modules a person may
 * use and how far each grant reaches: their own chain (the people and counters
 * below them) or the whole organization. The org admin always has everything;
 * the karigar is the fixed scan-and-redeem app and is never configured.
 */
export const PERMISSION_ACTIONS = ["view", "create", "edit", "delete"];
/** `own` — the caller's chain only. `org` — everything in the organization. */
export const PERMISSION_SCOPES = ["own", "org"];
export const PERMISSION_MODULES = [
    "overview",
    "team",
    "counters",
    "karigars",
    "approvals",
    "settlements",
    "payouts",
    "performance",
    "depots",
    "products",
    "rewards",
    "audit",
    "links",
    "dispatch",
    "broadcasts",
    "documents",
];
const CRUD = PERMISSION_ACTIONS;
export const MODULE_META = {
    // Pages that gather other modules' data: each figure on them still obeys that module's own grant.
    overview: { label: "Overview", actions: ["view"], chain: false },
    links: { label: "Links", actions: ["view"], chain: false },
    team: { label: "Sales team", actions: CRUD, chain: true },
    counters: { label: "Counters", actions: CRUD, chain: true },
    karigars: { label: "Karigars", actions: CRUD, chain: true, verbs: { edit: "Edit & transfer" } },
    approvals: { label: "KYC approvals", actions: ["view", "edit"], chain: true, verbs: { edit: "Approve / reject" } },
    settlements: { label: "Settlements", actions: ["view", "create"], chain: true, verbs: { create: "Record payment" } },
    payouts: { label: "Payouts", actions: ["view", "create"], chain: false, verbs: { create: "Pay karigars" } },
    performance: { label: "Performance", actions: ["view"], chain: false },
    depots: { label: "Depots", actions: CRUD, chain: false },
    products: { label: "Products", actions: ["view", "create", "edit"], chain: false },
    rewards: {
        label: "Reward codes",
        actions: CRUD,
        chain: false,
        verbs: { create: "Issue batches", edit: "Edit & reverse codes", delete: "Withdraw batches" },
    },
    audit: { label: "Audit log", actions: ["view"], chain: false },
    dispatch: {
        label: "Dispatch & stock",
        actions: ["view", "create", "delete"],
        chain: true,
        verbs: { create: "Send stock", delete: "Undo dispatch" },
    },
    broadcasts: { label: "Promotions", actions: ["view", "create"], chain: false },
    documents: {
        label: "Documents",
        actions: CRUD,
        chain: false,
        verbs: { create: "Upload", edit: "Edit & replace", delete: "Delete" },
    },
};
/** Switchable modules (D-20). Off, they vanish from menus, permissions and the API. */
export const FEATURES = ["dispatch"];
/** The feature a permission module belongs to, when it can be switched off. */
export const MODULE_FEATURE = { dispatch: "dispatch" };
/** The roles an org admin configures. */
export const CONFIGURABLE_ROLES = ["sales_rep", "distributor", "sub_distributor", "counter"];
export function isConfigurableRole(role) {
    return CONFIGURABLE_ROLES.includes(role);
}
/**
 * Exactly what each role could do before permissions were configurable, so an
 * org that never opens the permissions screen behaves as it always has.
 * The one addition: a distributor may list the sub-distributors they create.
 */
export const DEFAULT_PERMISSIONS = {
    sales_rep: { counters: { view: "own", create: "own" }, depots: { view: "org" } },
    distributor: {
        team: { view: "own", create: "own" },
        counters: { view: "own", create: "own" },
        depots: { view: "org" },
        dispatch: { view: "own", create: "own" },
    },
    sub_distributor: { counters: { view: "own", create: "own" }, dispatch: { view: "own", create: "own" } },
    counter: {
        karigars: { view: "own", create: "own" },
        payouts: { view: "org", create: "org" },
        dispatch: { view: "own" },
    },
};
/**
 * What a role can meaningfully be granted. A counter manages nobody and adds
 * no counters; a sales rep holds no stock (D-20). The Roles & permissions
 * panel hides the rest, and the API drops it on save.
 */
export function grantable(base, module, action) {
    if (module === "dispatch")
        return base !== "sales_rep";
    if (base === "counter")
        return module !== "team" && !(module === "counters" && action !== "view");
    return true;
}
/** `grid` without the grants `base` cannot use. */
export function grantableGrid(base, grid) {
    const out = {};
    for (const [module, grant] of Object.entries(grid)) {
        const kept = Object.fromEntries(Object.entries(grant ?? {}).filter(([action]) => grantable(base, module, action)));
        if (Object.keys(kept).length > 0)
            out[module] = kept;
    }
    return out;
}
/** The org admin's grid: every action, organization-wide. */
export const FULL_ACCESS = Object.fromEntries(PERMISSION_MODULES.map((m) => [m, Object.fromEntries(MODULE_META[m].actions.map((a) => [a, "org"]))]));
export function grantOf(grid, module, action) {
    return grid?.[module]?.[action] ?? null;
}
/** Only the sales roles are copied into levels; a counter is always level 1. */
function levelCount(tierLevels, base) {
    return base === "counter" ? 1 : levelsOf(tierLevels, base);
}
/** "Distributor" when there is one level, "Distributor 2" once it is copied. */
export function defaultRoleName(base, level, levels) {
    return levels > 1 ? `${ROLE_LABELS[base]} ${level}` : ROLE_LABELS[base];
}
export const DEFAULT_ENABLED_TIERS = ["sales_rep", "counter", "karigar"];
/** The tiers an org uses, including orgs from before `enabledTiers` existed. */
export function resolveEnabledTiers(settings) {
    const tiers = settings?.enabledTiers;
    if (tiers && tiers.length > 0)
        return [...tiers];
    return settings?.distributorEnabled ? ["sales_rep", "distributor", "counter", "karigar"] : [...DEFAULT_ENABLED_TIERS];
}
/** Every configurable role this org has, in chain order, with its name, permissions and manager resolved. */
export function buildRoleDefs(settings) {
    const chain = resolveChain(settings);
    return chain.flatMap((node) => {
        // A depot is a stock stop, not a person — it has no role to configure (D-19).
        if (node === "depot")
            return [];
        const [base, level] = node.split(":");
        if (!base || !isConfigurableRole(base))
            return [];
        return [{ ...roleDef(settings, base, Number(level ?? 1)), reportsTo: reportsTo(chain, node) }];
    });
}
/** One role's resolved definition — also used for people on a tier the org has since turned off. */
export function roleDef(settings, base, level, levels = levelCount(settings?.tierLevels, base)) {
    const key = roleKey(base, level);
    const stored = settings?.roles?.[key];
    return {
        key,
        base,
        level,
        name: stored?.name || defaultRoleName(base, level, levels),
        permissions: stored?.permissions ?? DEFAULT_PERMISSIONS[base],
        customized: Boolean(stored?.permissions),
        reportsTo: null,
    };
}
/* ── validation ──────────────────────────────────────────────────────── */
const scopeSchema = z.enum(PERMISSION_SCOPES);
const grantSchema = z
    .object({
    view: scopeSchema.optional(),
    create: scopeSchema.optional(),
    edit: scopeSchema.optional(),
    delete: scopeSchema.optional(),
})
    .strict();
export const permissionGridSchema = z
    .partialRecord(z.enum(PERMISSION_MODULES), grantSchema)
    .superRefine((grid, ctx) => {
    for (const [module, grant] of Object.entries(grid)) {
        const meta = MODULE_META[module];
        for (const [action, scope] of Object.entries(grant)) {
            if (!scope)
                continue;
            if (!meta.actions.includes(action)) {
                ctx.addIssue({ code: "custom", path: [module, action], message: `${meta.label} has no "${action}".` });
            }
            else if (scope === "own" && !meta.chain) {
                ctx.addIssue({ code: "custom", path: [module, action], message: `${meta.label} is organization-wide.` });
            }
        }
    }
});
/** `PATCH /org/roles/:key`. `permissions: null` puts the role back on its defaults. */
export const updateRoleSchema = z
    .object({
    name: nameSchema.optional(),
    permissions: permissionGridSchema.nullable().optional(),
})
    .refine((v) => v.name !== undefined || v.permissions !== undefined, "Nothing to change.");
//# sourceMappingURL=permissions.js.map